<template>
  <div class="p-6 bg-[#f9fafb] min-h-screen">
    <!-- Page Header -->
    <h1 class="text-2xl font-bold mb-6">Racing Results Query Tool</h1>
    
    <!-- Main content with two-column layout -->
    <div class="flex flex-col md:flex-row gap-6">
      <!-- Left Sidebar - Filters -->
      <div class="w-full md:w-1/4 md:max-w-xs">
        <div class="bg-white rounded-lg shadow-sm p-5 sticky top-6">
          <div>
            <h2 class="text-lg font-medium mb-4">Filters</h2>
            
            <form @submit.prevent="handleSearchSubmit" class="space-y-1">
              <!-- Date Range -->
              <div>
                <UiLabel htmlFor="dateRange" class="text-sm font-medium">Date Range</UiLabel>
                <div class="mt-2">
                  <Datepicker
                    v-model="dateRange"
                    range
                    format="yyyy-MM-dd"
                    :max-date="new Date()"
                    :enable-time-picker="false"
                    auto-apply
                    :partial-range="false"
                    placeholder="Select date range"
                    :clearable="true"
                    @update:model-value="updateDateRange"
                    input-class-name="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    class="w-full"
                  />
                  <div v-if="dateErrors.startDate || dateErrors.endDate" class="text-xs text-red-500 mt-1">
                    {{ dateErrors.startDate || dateErrors.endDate }}
                  </div>
                </div>

                <!-- Quick date selectors -->
                <div class="flex mt-2 space-x-2">
                  <button
                    type="button"
                    class="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-md bg-white hover:bg-gray-50"
                    @click="setLastWeek"
                  >
                    Last Week
                  </button>
                  <button
                    type="button"
                    class="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-md bg-white hover:bg-gray-50"
                    @click="setLastMonth"
                  >
                    Last Month
                  </button>
                  <button
                    type="button"
                    class="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-md bg-white hover:bg-gray-50"
                    @click="setLastSixMonth"
                  >
                    Last 6 Months
                  </button>
                </div>
              </div>

              <!-- User ID (Hidden but preserved for functionality) -->
              <input
                type="hidden"
                id="userId"
                v-model="searchParams.userId"
              >

              <!-- Accordion Filter Sections -->
              <AccordionRoot type="multiple" :default-value="['basic']" class="mt-4 space-y-1">

                <!-- Basic Filters -->
                <AccordionItem value="basic" class="border border-gray-200 rounded-md overflow-hidden">
                  <AccordionTrigger class="flex items-center justify-between w-full px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 transition-colors accordion-trigger">
                    Basic Filters
                    <ChevronDown class="h-4 w-4 text-gray-500 accordion-chevron transition-transform duration-200" />
                  </AccordionTrigger>
                  <AccordionContent class="accordion-content overflow-hidden">
                    <div class="px-3 py-3 space-y-3">

                      <!-- Trainers -->
                      <div>
                        <UiLabel class="text-sm font-medium">Trainers</UiLabel>
                        <div class="mt-1.5">
                          <MultiSelectCombobox
                            v-model="selectedTrainers"
                            :options="trainerOptions"
                            placeholder="Select trainers..."
                            track-by="id"
                            label-field="value"
                            :loading="isLoadingTrainers"
                            @search="searchTrainers"
                          />
                        </div>
                      </div>

                      <!-- Jockeys -->
                      <div>
                        <UiLabel class="text-sm font-medium">Jockeys</UiLabel>
                        <div class="mt-1.5">
                          <MultiSelectCombobox
                            v-model="selectedJockeys"
                            :options="jockeyOptions"
                            placeholder="Select jockeys..."
                            track-by="id"
                            label-field="value"
                            :loading="isLoadingJockeys"
                            @search="searchJockeys"
                          />
                        </div>
                      </div>

                      <!-- Courses -->
                      <div>
                        <UiLabel class="text-sm font-medium">Courses</UiLabel>
                        <div class="mt-1.5">
                          <MultiSelectCombobox
                            v-model="selectedCourses"
                            :options="courseOptions"
                            placeholder="Select courses..."
                            track-by="id"
                            label-field="value"
                            :loading="isLoadingCourses"
                            @search="searchCourses"
                          />
                        </div>
                      </div>

                      <!-- Regions -->
                      <div>
                        <UiLabel class="text-sm font-medium">Regions</UiLabel>
                        <div class="mt-1.5">
                          <MultiSelectCombobox
                            v-model="selectedRegions"
                            :options="regionOptions"
                            placeholder="Select regions..."
                            track-by="value"
                            label-field="value"
                            :loading="isLoadingRegions"
                            @search="searchRegions"
                          />
                        </div>
                      </div>

                      <!-- Types -->
                      <div>
                        <UiLabel class="text-sm font-medium">Types</UiLabel>
                        <div class="mt-1.5">
                          <MultiSelectCombobox
                            v-model="selectedTypes"
                            :options="typeOptions"
                            placeholder="Select types..."
                            track-by="value"
                            label-field="value"
                            :loading="isLoadingTypes"
                            @search="searchTypes"
                          />
                        </div>
                      </div>

                    </div>
                  </AccordionContent>
                </AccordionItem>

                <!-- Advanced Filters -->
                <AccordionItem value="advanced" class="border border-gray-200 rounded-md overflow-hidden">
                  <AccordionTrigger class="flex items-center justify-between w-full px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 transition-colors accordion-trigger">
                    Advanced Filters
                    <ChevronDown class="h-4 w-4 text-gray-500 accordion-chevron transition-transform duration-200" />
                  </AccordionTrigger>
                  <AccordionContent class="accordion-content overflow-hidden">
                    <div class="px-3 py-3 space-y-3">

                      <!-- Distance -->
                      <div>
                        <UiLabel class="text-sm font-medium">Distance</UiLabel>
                        <div class="mt-1.5">
                          <MultiSelectCombobox
                            v-model="selectedDistances"
                            :options="distanceOptions"
                            placeholder="Select distances..."
                            track-by="value"
                            label-field="value"
                            :loading="isLoadingDistances"
                            @search="searchDistances"
                          />
                        </div>
                      </div>

                      <!-- Going -->
                      <div>
                        <UiLabel class="text-sm font-medium">Going</UiLabel>
                        <div class="mt-1.5">
                          <MultiSelectCombobox
                            v-model="selectedGoings"
                            :options="goingOptions"
                            placeholder="Select goings..."
                            track-by="value"
                            label-field="value"
                            :loading="isLoadingGoings"
                            @search="searchGoings"
                          />
                        </div>
                      </div>

                      <!-- Surface -->
                      <div>
                        <UiLabel class="text-sm font-medium">Surface</UiLabel>
                        <div class="mt-1.5">
                          <MultiSelectCombobox
                            v-model="selectedSurfaces"
                            :options="surfaceOptions"
                            placeholder="Select surfaces..."
                            track-by="value"
                            label-field="value"
                            :loading="isLoadingSurfaces"
                            @search="searchSurfaces"
                          />
                        </div>
                      </div>

                      <!-- Age -->
                      <div>
                        <UiLabel class="text-sm font-medium">Age</UiLabel>
                        <div class="mt-1.5">
                          <MultiSelectCombobox
                            v-model="selectedAges"
                            :options="ageOptions"
                            placeholder="Select ages..."
                            track-by="value"
                            label-field="value"
                            :loading="isLoadingAges"
                            @search="searchAges"
                          />
                        </div>
                      </div>

                      <!-- Sex -->
                      <div>
                        <UiLabel class="text-sm font-medium">Sex</UiLabel>
                        <div class="mt-1.5">
                          <MultiSelectCombobox
                            v-model="selectedSexes"
                            :options="sexOptions"
                            placeholder="Select sexes..."
                            track-by="value"
                            label-field="value"
                            :loading="isLoadingSexes"
                            @search="searchSexes"
                          />
                        </div>
                      </div>

                      <!-- Sires -->
                      <div>
                        <UiLabel class="text-sm font-medium">Sires</UiLabel>
                        <div class="mt-1.5">
                          <MultiSelectCombobox
                            v-model="selectedSires"
                            :options="sireOptions"
                            placeholder="Select sires..."
                            track-by="id"
                            label-field="value"
                            :loading="isLoadingSires"
                            @search="searchSires"
                          />
                        </div>
                      </div>

                    </div>
                  </AccordionContent>
                </AccordionItem>

              </AccordionRoot>

              <!-- Button Row: Save as System and Search -->
              <div class="flex gap-3 mb-4 mt-4">
                <!-- Save as System Button -->
                <button 
                  type="button"
                  class="flex-1 px-3 py-2 text-sm font-medium border border-gray-300 rounded-md bg-white hover:bg-gray-50 text-center"
                  @click="openSaveSystemModal"
                >
                  Save
                </button>
                
                <!-- Search Button -->
                <button 
                  type="submit" 
                  class="flex-1 inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500" 
                  :disabled="loading"
                >
                  <svg v-if="loading" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Search
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      
      <!-- Right Content Area -->
      <div class="w-full md:w-3/4 bg-white rounded-lg shadow-sm p-5">
        <!-- Stats Cards -->
        <div v-if="results && results.length > 0 && !loading" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <!-- Total Selections -->
          <div class="bg-white border rounded-lg shadow-sm">
            <div class="p-6">
              <h3 class="text-sm font-medium text-gray-500 mb-1">Total Selections</h3>
              <p class="text-3xl font-bold">{{ totalItems }}</p>
            </div>
          </div>
      
          <!-- Total Winners -->
          <div class="bg-white border rounded-lg shadow-sm">
            <div class="p-6">
              <h3 class="text-sm font-medium text-gray-500 mb-1">Total Winners</h3>
              <p class="text-3xl font-bold">{{ stats.totalWinners }}</p>
            </div>
          </div>
      
          <!-- Strike Rate -->
          <div class="bg-white border rounded-lg shadow-sm">
            <div class="p-6">
              <h3 class="text-sm font-medium text-gray-500 mb-1">Strike Rate</h3>
              <p class="text-3xl font-bold">{{ stats.strikeRate.toFixed(2) }}%</p>
            </div>
          </div>
      
          <!-- Total Profit/Loss -->
          <div class="bg-white border rounded-lg shadow-sm">
            <div class="p-6">
              <h3 class="text-sm font-medium text-gray-500 mb-1">Total Profit/Loss</h3>
              <p class="text-3xl font-bold" :class="stats.totalProfitLoss >= 0 ? 'text-green-500' : 'text-red-500'">
                {{ stats.totalProfitLoss >= 0 ? '+' : '' }}{{ stats.totalProfitLoss.toFixed(2) }}
              </p>
            </div>
          </div>
        </div>
    
        <!-- Charts Section -->
        <div v-if="results.length > 0 && !loading" class="mb-6">
          <!-- Monthly Stats Chart -->
          <div class="bg-white border rounded-lg shadow-sm w-full">
            <div class="p-6">
              <MonthlyStatsChart :data="monthlyStatsData" />
            </div>
          </div>
        </div>
    
        <!-- Loading indicator -->
        <div v-if="loading" class="flex justify-center items-center py-12">
          <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
          <p class="ml-3 text-gray-600">Searching...</p>
        </div>
        
        <!-- Error message -->
        <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <div class="flex">
            <svg class="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
            </svg>
            <p class="ml-3 text-sm text-red-700">{{ error }}</p>
          </div>
        </div>
        
        <!-- No results message -->
        <div v-else-if="results && results.length === 0 && searched" class="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
          <div class="flex">
            <svg class="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9a1 1 0 00-1-1z" clip-rule="evenodd" />
            </svg>
            <p class="ml-3 text-sm text-blue-700">No racing results found for the selected criteria</p>
          </div>
        </div>
    
        <!-- Results Table -->
        <div v-else-if="results && results.length > 0" class="space-y-6">
          <!-- Results count -->
          <!-- <div class="flex justify-between items-center">
            <p class="text-sm text-gray-600">Found {{ results.length }} results</p>
          </div> -->
          
          <!-- Table -->
          <div class="overflow-x-auto rounded-lg border border-gray-200">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Course</th>
                  <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Horse</th>
                  <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Position</th>
                  <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SP</th>
                  <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dec</th>
                  <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Jockey</th>
                  <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Trainer</th>
                  <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Result</th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-for="(race, index) in results" :key="index" class="hover:bg-gray-50">
                  <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{{ formatDate(race.date) }}</td>
                  <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{{ race.course }}</td>
                  <td class="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{{ race.horse }}</td>
                  <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-900" :class="positionClass(race.pos)">{{ race.pos }}</td>
                  <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{{ race.sp }}</td>
                  <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{{ race.dec }}</td>
                  <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{{ race.jockey }}</td>
                  <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{{ race.trainer }}</td>
                  <td class="px-4 py-3 whitespace-nowrap text-sm">
                    <span v-if="race.win" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Winner</span>
                    <span v-else class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Loser</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
      
          <!-- Pagination -->
          <div v-if="totalPages > 1" class="flex justify-center space-x-1 mt-6">
            <button 
              class="px-3 py-1 rounded-md text-sm font-medium border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="currentPage === 1"
              @click="changePage(currentPage - 1)"
            >
              Previous
            </button>
            
            <!-- First Page -->
            <button 
              v-if="totalPages > 0"
              class="px-3 py-1 rounded-md text-sm font-medium border"
              :class="1 === currentPage ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'"
              @click="changePage(1)"
            >
              1
            </button>
            
            <!-- Left Ellipsis -->
            <span v-if="currentPage > 4" class="px-2 py-1 text-gray-500">...</span>
            
            <!-- Pages around current page -->
            <template v-for="page in totalPages" :key="page">
              <button 
                v-if="page !== 1 && page !== totalPages && page >= currentPage - 1 && page <= currentPage + 1"
                class="px-3 py-1 rounded-md text-sm font-medium border"
                :class="page === currentPage ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'"
                @click="changePage(page)"
              >
                {{ page }}
              </button>
            </template>
            
            <!-- Right Ellipsis -->
            <span v-if="currentPage < totalPages - 3" class="px-2 py-1 text-gray-500">...</span>
            
            <!-- Last Page -->
            <button 
              v-if="totalPages > 1"
              class="px-3 py-1 rounded-md text-sm font-medium border"
              :class="totalPages === currentPage ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'"
              @click="changePage(totalPages)"
            >
              {{ totalPages }}
            </button>
            
            <button 
              class="px-3 py-1 rounded-md text-sm font-medium border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="currentPage === totalPages"
              @click="changePage(currentPage + 1)"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Save System Modal -->
  <div v-if="showSaveSystemModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
    <div class="bg-white rounded-lg max-w-md w-full p-6 shadow-xl">
      <h3 class="text-lg font-medium mb-4">Save System</h3>
      
      <form @submit.prevent="saveSystem">
        <!-- System Name -->
        <div class="mb-4">
          <label for="systemName" class="block text-sm font-medium text-gray-700 mb-1">Name</label>
          <input 
            type="text" 
            id="systemName" 
            v-model="systemForm.name" 
            class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            required
          />
        </div>
        
        <!-- System Description -->
        <div class="mb-4">
          <label for="systemDescription" class="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <textarea 
            id="systemDescription" 
            v-model="systemForm.description" 
            rows="3" 
            class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          ></textarea>
        </div>
        
        <!-- Buttons -->
        <div class="flex justify-end space-x-2 mt-6">
          <button 
            type="button" 
            class="px-4 py-2 text-sm font-medium border border-gray-300 rounded-md hover:bg-gray-50"
            @click="showSaveSystemModal = false"
          >
            Cancel
          </button>
          <button 
            type="submit" 
            class="px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            :disabled="savingSystem"
          >
            <span v-if="savingSystem">Saving...</span>
            <span v-else>Save System</span>
          </button>
        </div>
      </form>
    </div>
  </div>

</template>

<script>
import { ref, reactive, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import apiService from '@/services/api.service';
import Datepicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import MultiSelectCombobox from '@/components/ui/multi-select-combobox.vue';
import { AccordionRoot, AccordionItem, AccordionTrigger, AccordionContent } from 'radix-vue';
import { ChevronDown } from 'lucide-vue-next';
import MonthlyStatsChart from '@/components/MonthlyStatsChart.vue';

export default {
  name: 'SearchResultsView',
  components: {
    Datepicker,
    MultiSelectCombobox,
    AccordionRoot,
    AccordionItem,
    AccordionTrigger,
    AccordionContent,
    ChevronDown,
    MonthlyStatsChart
  },
  setup() {
    const isLoadingTrainers = ref(false);
    const isLoadingJockeys = ref(false);
    const isLoadingCourses = ref(false);
    const isLoadingRegions = ref(false);
    const isLoadingTypes = ref(false);
    const isLoadingDistances = ref(false);
    const isLoadingGoings = ref(false);
    const isLoadingSurfaces = ref(false);
    const isLoadingAges = ref(false);
    const isLoadingSexes = ref(false);
    const isLoadingSires = ref(false);

    const route = useRoute();
    const router = useRouter();
    const store = useStore();

    const userId = store.state.user?.id;
    
    // Initialize all state variables first
    const searchParams = reactive({
      StartDate: '',
      EndDate: '',
      Course: '',
      Jockey: '',
      Trainer: '',
      Region: '',
      Trainers: [],
      Jockeys: [],
      Courses: [],
      Regions: [],
      Types: [],
      Distances: [],
      Goings: [],
      Surfaces: [],
      Ages: [],
      Sexes: [],
      Sires: []
    });
    const lastSearchedParams = ref({});
    const results = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const currentPage = ref(1);
    const pageSize = ref(15); 
    const totalItems = ref(0);
    const totalPages = ref(0); // Changed from computed to ref
    const searched = ref(false);
    
    // System saving
    const showSaveSystemModal = ref(false);
    const savingSystem = ref(false);
    const systemForm = reactive({
      name: '',
      description: ''
    });

    // Statistics from API
    const stats = reactive({
      totalWinners: 0,
      strikeRate: 0,
      totalProfitLoss: 0
    }); 
    const dateErrors = reactive({
      startDate: '',
      endDate: ''
    });
    
    // Date range for the date picker component
    const dateRange = ref(null);


    // Check if there's a system filter to load - optimized to not make duplicate API calls
    const loadSavedFilters = async () => {
      const savedFilters = localStorage.getItem('activeSystemFilters');

      if (savedFilters) {
        try {
          // Parse the saved filters
          const parsedFilters = JSON.parse(savedFilters);
          
          // Clear local storage to prevent reloading on future visits
          localStorage.removeItem('activeSystemFilters');
          
          // Apply the saved filters to searchParams
          Object.keys(parsedFilters).forEach(key => {
            if (Object.prototype.hasOwnProperty.call(searchParams, key)) {
              searchParams[key] = parsedFilters[key];
            }
          });
          
          // Update date range if present
          if (parsedFilters.createdAt) {
            const startDate = new Date(parsedFilters.createdAt);
            // set endDate to today  + 1 day
            const endDate = new Date();
            endDate.setDate(endDate.getDate() + 1);
            dateRange.value = [startDate, endDate];

             // Important: Also update the searchParams that will be used for the API call
            searchParams.StartDate = formatDateForApi(startDate);
            searchParams.EndDate = formatDateForApi(endDate);
          }
          
          // Restore selected filter values from saved IDs/values.
          // Options are no longer pre-loaded on mount, so we build lightweight
          // stub objects here. The API calls use .id or .value which are correct.
          // For ID-based filters (trainers, jockeys, courses, sires) the tag label
          // will show the numeric ID until the user re-searches; value-based filters
          // (regions, types, etc.) store the actual display string so tags look correct.

          if (parsedFilters.Trainers && parsedFilters.Trainers.length > 0) {
            selectedTrainers.value = parsedFilters.Trainers.map(id => ({ id, value: String(id) }));
          }

          if (parsedFilters.Jockeys && parsedFilters.Jockeys.length > 0) {
            selectedJockeys.value = parsedFilters.Jockeys.map(id => ({ id, value: String(id) }));
          }

          if (parsedFilters.Courses && parsedFilters.Courses.length > 0) {
            selectedCourses.value = parsedFilters.Courses.map(id => ({ id, value: String(id) }));
          }

          if (parsedFilters.Regions && parsedFilters.Regions.length > 0) {
            selectedRegions.value = parsedFilters.Regions.map(v => ({ value: v }));
          }

          if (parsedFilters.Types && parsedFilters.Types.length > 0) {
            selectedTypes.value = parsedFilters.Types.map(v => ({ value: v }));
          }

          if (parsedFilters.Distances && parsedFilters.Distances.length > 0) {
            selectedDistances.value = parsedFilters.Distances.map(v => ({ value: v }));
          }

          if (parsedFilters.Goings && parsedFilters.Goings.length > 0) {
            selectedGoings.value = parsedFilters.Goings.map(v => ({ value: v }));
          }

          if (parsedFilters.Surfaces && parsedFilters.Surfaces.length > 0) {
            selectedSurfaces.value = parsedFilters.Surfaces.map(v => ({ value: v }));
          }

          if (parsedFilters.Ages && parsedFilters.Ages.length > 0) {
            selectedAges.value = parsedFilters.Ages.map(v => ({ value: v }));
          }

          if (parsedFilters.Sexes && parsedFilters.Sexes.length > 0) {
            selectedSexes.value = parsedFilters.Sexes.map(v => ({ value: v }));
          }

          if (parsedFilters.Sires && parsedFilters.Sires.length > 0) {
            selectedSires.value = parsedFilters.Sires.map(v => ({ value: v }));
          }
          
          return true; // Indicate filters were loaded successfully
        } catch (err) {
          console.error('Error loading saved filters:', err);
          error.value = 'Failed to load saved filters';
          return false;
        }
      }
      return false; // No filters were loaded
    };
    
    // Methods
    const resetSearch = () => {
      // Preserve the user ID when clearing other search params
      const userId = searchParams.userId;
      Object.keys(searchParams).forEach(key => {
        if (key === 'Trainers' || key === 'Jockeys' || key === 'Courses' || key === 'Regions' || 
            key === 'Distances' || key === 'Goings' || key === 'Surfaces' || 
            key === 'Ages' || key === 'Sexes' || key === 'Sires') {
          searchParams[key] = [];
        } else {
          searchParams[key] = '';
        }
      });
      searchParams.userId = userId;
      error.value = null;
      
      // Clear date errors
      dateErrors.startDate = '';
      dateErrors.endDate = '';
      
      // Reset selected items
      selectedTrainers.value = [];
      selectedJockeys.value = [];
      selectedCourses.value = [];
      selectedRegions.value = [];
      selectedTypes.value = [];
      selectedDistances.value = [];
      selectedGoings.value = [];
      selectedSurfaces.value = [];
      selectedAges.value = [];
      selectedSexes.value = [];
      selectedSires.value = [];
    };
    
    // Format date for API requests (YYYY-MM-DD)
    const formatDateForApi = (input) => {
      if (!input) return '';
      
      // If input is a Date object, convert to YYYY-MM-DD format
      if (input instanceof Date) {
        const year = input.getFullYear();
        const month = String(input.getMonth() + 1).padStart(2, '0');
        const day = String(input.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
      
      // If already a string, assume it's already formatted correctly
      return input;
    };
    
    // Get current date in YYYY-MM-DD format to use as max date
    /* eslint-disable-next-line no-unused-vars */
    const getMaxDate = () => {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    // Update search params from the date picker
    const updateDateRange = (range) => {
      if (range && Array.isArray(range) && range.length === 2) {
        searchParams.StartDate = formatDateForApi(range[0]);
        searchParams.EndDate = formatDateForApi(range[1]);
      } else {
        // Clear dates if range is invalid or cleared
        searchParams.StartDate = '';
        searchParams.EndDate = '';
      }
      validateDates();
    };
    
    // Set date range to last week
    const setLastWeek = () => {
      const today = new Date();
      const lastWeek = new Date(today);
      lastWeek.setDate(today.getDate() - 7);
      
      searchParams.StartDate = formatDateForApi(lastWeek);
      searchParams.EndDate = formatDateForApi(today);
      
      // Update the date picker value
      dateRange.value = [lastWeek, today];
      
      validateDates();
    };
    
    // Set date range to last month
    const setLastMonth = () => {
      const today = new Date();
      const lastMonth = new Date(today);
      lastMonth.setMonth(today.getMonth() - 1);
      
      searchParams.StartDate = formatDateForApi(lastMonth);
      searchParams.EndDate = formatDateForApi(today);
      
      // Update the date picker value
      dateRange.value = [lastMonth, today];
      
      validateDates();
    };

    // Set date range to last month
    const setLastSixMonth = () => {
      const today = new Date();
      const lastMonth = new Date(today);
      lastMonth.setMonth(today.getMonth() - 6);
      
      searchParams.StartDate = formatDateForApi(lastMonth);
      searchParams.EndDate = formatDateForApi(today);
      
      // Update the date picker value
      dateRange.value = [lastMonth, today];
      
      validateDates();
    };
    
    // Validate date range
    const validateDates = () => {
      dateErrors.startDate = '';
      dateErrors.endDate = '';
      
      if (searchParams.StartDate && searchParams.EndDate) {
        const start = new Date(searchParams.StartDate);
        const end = new Date(searchParams.EndDate);
        
        if (start > end) {
          dateErrors.startDate = 'Start date cannot be after end date';
          return false;
        }
      }
      
      return true;
    };
    
    const handleSearchSubmit = () => {
      performSearch(1); // Always search page 1 on new form submission
    };

    const performSearch = async (page = 1) => {

      const userId = store.state.user?.id;
      if (!userId) {
          error.value = 'User not authenticated or user ID not found.';
          // Optionally redirect to login
          router.push('/login');
          return;
      }

      // Validate date range before submitting
      if (!validateDates()) {
        return;
      }
      
      loading.value = true;
      error.value = null;
      currentPage.value = page;
      searched.value = true;
      
      // Create the params object in the desired format
      const params = {};

      // Add userId from store
      params.userId = userId;

      // Add Pagination parameters (ensure casing matches backend)
      params.PageNumber = page;
      params.PageSize = pageSize.value;

      // Handle Dates first
      if (searchParams.StartDate) {
        params.startDate = formatDateForApi(searchParams.StartDate); // Assuming lowercase key for backend
      }
      if (searchParams.EndDate) {
        params.endDate = formatDateForApi(searchParams.EndDate); // Assuming lowercase key for backend
      }

      // Handle ID arrays
      if (searchParams.Trainers && Array.isArray(searchParams.Trainers) && searchParams.Trainers.length > 0) {
        params.trainers = searchParams.Trainers.map(item => item.id);
      }
      if (searchParams.Jockeys && Array.isArray(searchParams.Jockeys) && searchParams.Jockeys.length > 0) {
        params.jockeys = searchParams.Jockeys.map(item => item.id);
      }
      if (searchParams.Courses && Array.isArray(searchParams.Courses) && searchParams.Courses.length > 0) {
        params.courses = searchParams.Courses.map(item => item.id);
      }
      // Add Regions if needed - using value property since regions don't have id
      if (searchParams.Regions && Array.isArray(searchParams.Regions) && searchParams.Regions.length > 0) {
         params.regions = searchParams.Regions.map(item => item.value); // Using value instead of name for regions
      }
      // Add Types if needed - using value property since types don't have id
      if (searchParams.Types && Array.isArray(searchParams.Types) && searchParams.Types.length > 0) {
         params.types = searchParams.Types.map(item => item.value); // Using value instead of name for types
      }
      // Add Distances if needed - using value property since distances don't have id
      if (searchParams.Distances && Array.isArray(searchParams.Distances) && searchParams.Distances.length > 0) {
        params.distances = searchParams.Distances.map(item => item.value); // Using value instead of name for distances
      }
      // Add Goings if needed - using value property since goings don't have id
      if (searchParams.Goings && Array.isArray(searchParams.Goings) && searchParams.Goings.length > 0) {
         params.goings = searchParams.Goings.map(item => item.value); // Using value instead of name for goings
      }
      // Add Surfaces if needed - using value property since surfaces don't have id
      if (searchParams.Surfaces && Array.isArray(searchParams.Surfaces) && searchParams.Surfaces.length > 0) {
         params.surfaces = searchParams.Surfaces.map(item => item.value); // Using value instead of name for surfaces
      }
      // Add Ages if needed - using value property since ages don't have id
      if (searchParams.Ages && Array.isArray(searchParams.Ages) && searchParams.Ages.length > 0) {
         params.ages = searchParams.Ages.map(item => item.value); // Using value instead of name for ages
      }
      // Add Sexes if needed - using value property since sexes don't have id
      if (searchParams.Sexes && Array.isArray(searchParams.Sexes) && searchParams.Sexes.length > 0) {
         params.sexes = searchParams.Sexes.map(item => item.value); // Using value instead of name for sexes
      }
      // Add Sires if needed - using value property since sires don't have id
      if (searchParams.Sires && Array.isArray(searchParams.Sires) && searchParams.Sires.length > 0) {
         params.sires = searchParams.Sires.map(item => item.value); // Using value instead of name for sires
      }


      // Handle any other flat parameters (ensure keys match backend expectations)
      Object.entries(searchParams).forEach(([key, value]) => {
         // Only add keys not already handled (Dates, Trainers, Jockeys, Courses, Regions, userId)
         const lowerKey = key.toLowerCase(); // Or specific mapping if needed
         if (value && !['startdate', 'enddate', 'trainers', 'jockeys', 'courses', 'regions', 'types', 'distances', 'goings', 'surfaces', 'ages', 'sexes', 'sires', 'userid'].includes(lowerKey)) {
           // You might need to adjust the key name here if the backend expects camelCase or PascalCase
           params[lowerKey] = value;
         }
      });
      
      console.log('Searching with params:', params);
      
      try {
        // Call the actual API endpoint using the apiService
        const response = await apiService.search(params);

        // Process the API response (adjust keys if needed)
        results.value = response.data?.results?.items || []; 
        totalItems.value = response.data?.results?.totalCount || 0;
        totalPages.value = response.data?.results?.totalPages || 0; // Get totalPages directly from API
        
        // Extract statistics if available
        if (response.data?.statistics) {
          stats.totalWinners = response.data.statistics.totalWinners || 0;
          stats.strikeRate = response.data.statistics.strikeRate || 0;
          stats.totalProfitLoss = response.data.statistics.totalProfitLoss || 0;
          processMonthlyStats(response.data.monthlyStats);
        }

        lastSearchedParams.value = { ...params };
        
      } catch (err) {
        console.error('Search error:', err);
        error.value = err.message || 'An error occurred while searching. Please try again.';
        results.value = [];
      } finally {
        loading.value = false;
      }
    };
    
    const changePage = (page) => {
      if (page >= 1 && page <= totalPages.value && page !== currentPage.value) {
        performSearch(page);
      }
    };
    
    const formatDate = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    };
    
    const positionClass = (position) => {
      if (position === '1') return 'text-success fw-bold';
      if (position === '2') return 'text-primary';
      if (position === '3') return 'text-info';
      return '';
    };
    
    // Initialize from URL params
    onMounted(async () => {

      // Validate dates first
      validateDates();

      // Load saved filters if present
      const filtersLoaded = await loadSavedFilters();

      // Get user ID from store if available
      const currentUser = store.getters.currentUser;
      if (currentUser && currentUser.id) {
        searchParams.userId = currentUser.id;
      }
      
      const query = route.query;
      if (Object.keys(query).length > 0) {
        // Map URL params to search params
        Object.keys(searchParams).forEach(key => {
          if (query[key]) {
            searchParams[key] = query[key];
          }
        });
      }

      // Now that everything is loaded, perform the search
      if (filtersLoaded || Object.keys(query).length > 0) {
        console.log("Saved Filters are fully loaded - now safe to search");
        performSearch();
      }
    });
    
    // Watch for route changes
    watch(() => route.query, (newQuery) => {
      if (Object.keys(newQuery).length > 0 && 
          JSON.stringify(newQuery) !== JSON.stringify(lastSearchedParams.value)) {
        // Map URL params to search params
        Object.keys(searchParams).forEach(key => {
          searchParams[key] = newQuery[key] || '';
        });
        performSearch();
      }
    });
    
    // Multi-select filter options
    const trainerOptions = ref([]);
    const jockeyOptions = ref([]);
    const courseOptions = ref([]);
    const regionOptions = ref([]);
    const typeOptions = ref([]);
    const distanceOptions = ref([]);
    const goingOptions = ref([]);
    const surfaceOptions = ref([]);
    const ageOptions = ref([]);
    const sexOptions = ref([]);
    const sireOptions = ref([]);
    
    // Selected values for multi-select dropdowns
    const selectedTrainers = ref([]);
    const selectedJockeys = ref([]);
    const selectedCourses = ref([]);
    const selectedRegions = ref([]);
    const selectedTypes = ref([]);
    const selectedDistances = ref([]);
    const selectedGoings = ref([]);
    const selectedSurfaces = ref([]);
    const selectedAges = ref([]);
    const selectedSexes = ref([]);
    const selectedSires = ref([]);
    
    // Watch for changes to selected values and update searchParams
    watch(selectedTrainers, (newVal) => {
      searchParams.Trainers = newVal || [];
    });
    
    watch(selectedJockeys, (newVal) => {
      searchParams.Jockeys = newVal || [];
    });
    
    watch(selectedCourses, (newVal) => {
      searchParams.Courses = newVal || [];
    });
    
    watch(selectedRegions, (newVal) => {
      searchParams.Regions = newVal || [];
    });

    watch(selectedTypes, (newVal) => {
      searchParams.Types = newVal || [];
    });
    
    watch(selectedDistances, (newVal) => {
      searchParams.Distances = newVal || [];
    });
    
    watch(selectedGoings, (newVal) => {
      searchParams.Goings = newVal || [];
    });
    
    watch(selectedSurfaces, (newVal) => {
      searchParams.Surfaces = newVal || [];
    });
    
    watch(selectedAges, (newVal) => {
      searchParams.Ages = newVal || [];
    });
    
    watch(selectedSexes, (newVal) => {
      searchParams.Sexes = newVal || [];
    });
    
    watch(selectedSires, (newVal) => {
      searchParams.Sires = newVal || [];
    });
    
    // Fetch options for each filter
    const fetchTrainers = async (searchTerm = '') => {
      try {
        const response = await apiService.getTrainers(searchTerm);
        // Preserve the exact order returned from the API
        trainerOptions.value = [...(response.data || [])];
        return response;
      } catch (err) {
        console.error('Error fetching trainers:', err);
        throw err;
      }
    };
    
    const fetchJockeys = async (searchTerm = '') => {
      try {
        const response = await apiService.getJockeys(searchTerm);
        jockeyOptions.value = response.data || [];
        return response;
      } catch (err) {
        console.error('Error fetching jockeys:', err);
        throw err;
      }
    };
    
    const fetchCourses = async (searchTerm = '') => {
      try {
        const response = await apiService.getCourses(searchTerm);
        courseOptions.value = response.data || [];
        return response;
      } catch (err) {
        console.error('Error fetching courses:', err);
        throw err;
      }
    };
    
    const fetchRegions = async (searchTerm = '') => {
      try {
        const response = await apiService.getRegions(searchTerm);
        regionOptions.value = response.data || [];
        return response;
      } catch (err) {
        console.error('Error fetching regions:', err);
        throw err;
      }
    };

    const fetchTypes = async (searchTerm = '') => {
      try {
        const response = await apiService.getTypes(searchTerm);
        typeOptions.value = response.data || [];
        return response;
      } catch (err) {
        console.error('Error fetching types:', err);
        throw err;
      }
    };
    
    const fetchDistances = async (searchTerm = '') => {
      try {
        const response = await apiService.getDistances(searchTerm);
        distanceOptions.value = response.data || [];
        return response;
      } catch (err) {
        console.error('Error fetching distances:', err);
        throw err;
      }
    };
    
    const fetchGoings = async (searchTerm = '') => {
      try {
        const response = await apiService.getGoings(searchTerm);
        goingOptions.value = response.data || [];
        return response;
      } catch (err) {
        console.error('Error fetching goings:', err);
        throw err;
      }
    };
    
    const fetchSurfaces = async (searchTerm = '') => {
      try {
        const response = await apiService.getSurfaces(searchTerm);
        surfaceOptions.value = response.data || [];
        return response;
      } catch (err) {
        console.error('Error fetching surfaces:', err);
        throw err;
      }
    };
    
    const fetchAges = async (searchTerm = '') => {
      try {
        const response = await apiService.getAges(searchTerm);
        ageOptions.value = response.data || [];
        return response;
      } catch (err) {
        console.error('Error fetching ages:', err);
        throw err;
      }
    };
    
    const fetchSexes = async (searchTerm = '') => {
      try {
        const response = await apiService.getSexes(searchTerm);
        sexOptions.value = response.data || [];
        return response;
      } catch (err) {
        console.error('Error fetching sexes:', err);
        throw err;
      }
    };
    
    const fetchSires = async (searchTerm = '') => {
      try {
        const response = await apiService.getSires(searchTerm);
        sireOptions.value = response.data || [];
        return response;
      } catch (err) {
        console.error('Error fetching sires:', err);
        throw err;
      }
    };
    
    // Search functions (debounce is handled inside MultiSelectCombobox)
    const searchTrainers = (searchTerm) => {
      isLoadingTrainers.value = true;
      fetchTrainers(searchTerm).finally(() => { isLoadingTrainers.value = false; });
    };

    const searchJockeys = (searchTerm) => {
      isLoadingJockeys.value = true;
      fetchJockeys(searchTerm).finally(() => { isLoadingJockeys.value = false; });
    };

    const searchCourses = (searchTerm) => {
      isLoadingCourses.value = true;
      fetchCourses(searchTerm).finally(() => { isLoadingCourses.value = false; });
    };

    const searchRegions = (searchTerm) => {
      isLoadingRegions.value = true;
      fetchRegions(searchTerm).finally(() => { isLoadingRegions.value = false; });
    };

    const searchTypes = (searchTerm) => {
      isLoadingTypes.value = true;
      fetchTypes(searchTerm).finally(() => { isLoadingTypes.value = false; });
    };

    const searchDistances = (searchTerm) => {
      isLoadingDistances.value = true;
      fetchDistances(searchTerm).finally(() => { isLoadingDistances.value = false; });
    };

    const searchGoings = (searchTerm) => {
      isLoadingGoings.value = true;
      fetchGoings(searchTerm).finally(() => { isLoadingGoings.value = false; });
    };

    const searchSurfaces = (searchTerm) => {
      isLoadingSurfaces.value = true;
      fetchSurfaces(searchTerm).finally(() => { isLoadingSurfaces.value = false; });
    };

    const searchAges = (searchTerm) => {
      isLoadingAges.value = true;
      fetchAges(searchTerm).finally(() => { isLoadingAges.value = false; });
    };

    const searchSexes = (searchTerm) => {
      isLoadingSexes.value = true;
      fetchSexes(searchTerm).finally(() => { isLoadingSexes.value = false; });
    };

    const searchSires = (searchTerm) => {
      isLoadingSires.value = true;
      fetchSires(searchTerm).finally(() => { isLoadingSires.value = false; });
    };
    
    // Clear all selected items functions
    const clearAllTrainers = () => {
      selectedTrainers.value = [];
    };
    
    const clearAllJockeys = () => {
      selectedJockeys.value = [];
    };
    
    const clearAllCourses = () => {
      selectedCourses.value = [];
    };
    
    const clearAllRegions = () => {
      selectedRegions.value = [];
    };

    const clearAllTypes = () => {
      selectedTypes.value = [];
    };
    
    const clearAllDistances = () => {
      selectedDistances.value = [];
    };
    
    const clearAllGoings = () => {
      selectedGoings.value = [];
    };
    
    const clearAllSurfaces = () => {
      selectedSurfaces.value = [];
    };
    
    const clearAllAges = () => {
      selectedAges.value = [];
    };
    
    const clearAllSexes = () => {
      selectedSexes.value = [];
    };
    
    const clearAllSires = () => {
      selectedSires.value = [];
    };
    
    // Limit text formatter
    const limitText = (count) => {
      return `and ${count} more items`;
    };

    // Add this to your setup function
    const monthlyStatsData = reactive({
      labels: [],
      selections: [],
      wins: [],
      monthlyReturn: [],
      profit: []
    });

    // Add a function to process monthly stats data
    const processMonthlyStats = (results) => {

      // Check if results contains the new API response format
      if (results) {
        // Process the new API response format
        processApiMonthlyStats(results);
        return;
      }
      
    };
  
    // Process the new API response format
    const processApiMonthlyStats = (monthlyStats) => {
      
      console.log('Processing API monthly stats:', monthlyStats);
      
      const months = [];
      const selections = [];
      const wins = [];
      const monthlyReturn = [];
      const profit = [];
      let rollingProfit = 0;
      
      // Process each month from the API response
      monthlyStats.forEach(monthData => {
        months.push(monthData.month);
        selections.push(monthData.selections);
        wins.push(monthData.wins);
        monthlyReturn.push(monthData.return);
        
        rollingProfit += monthData.return;
        profit.push(rollingProfit);
      });
      
      // Update the chart data
      monthlyStatsData.labels = months;
      monthlyStatsData.selections = selections;
      monthlyStatsData.wins = wins;
      monthlyStatsData.monthlyReturn = monthlyReturn;
      monthlyStatsData.profit = profit;
    };
    
    // Open the save system modal
    const openSaveSystemModal = () => {
      // Only allow saving if there's something to save
      if (Object.values(searchParams).some(val => 
        (Array.isArray(val) && val.length > 0) || 
        (typeof val === 'string' && val !== '')
      )) {
        showSaveSystemModal.value = true;
      } else {
        error.value = "Please apply at least one filter before saving a system.";
        setTimeout(() => {
          error.value = null;
        }, 3000);
      }
    };
    
    // Save the system
    const saveSystem = async () => {
      try {
        savingSystem.value = true;
        error.value = null;
        
        // Create a copy of searchParams and remove date filters for the JSON storage
        const searchParamsForFilters = { ...searchParams };
        const startDate = searchParamsForFilters.StartDate;
        const endDate = searchParamsForFilters.EndDate;
        
        // Remove date fields from filters JSON
        delete searchParamsForFilters.StartDate;
        delete searchParamsForFilters.EndDate;


        // Transform complex objects to primitive types
        // Convert array of objects to array of IDs or values
        if (searchParamsForFilters.Trainers && searchParamsForFilters.Trainers.length > 0) {
          searchParamsForFilters.Trainers = searchParamsForFilters.Trainers.map(t => t.id);
        }
        if (searchParamsForFilters.Jockeys && searchParamsForFilters.Jockeys.length > 0) {
          searchParamsForFilters.Jockeys = searchParamsForFilters.Jockeys.map(j => j.id);
        }
        if (searchParamsForFilters.Courses && searchParamsForFilters.Courses.length > 0) {
          searchParamsForFilters.Courses = searchParamsForFilters.Courses.map(c => c.id);
        }
        if (searchParamsForFilters.Regions && searchParamsForFilters.Regions.length > 0) {
          searchParamsForFilters.Regions = searchParamsForFilters.Regions.map(r => r.value);
        }
        if (searchParamsForFilters.Types && searchParamsForFilters.Types.length > 0) {
          searchParamsForFilters.Types = searchParamsForFilters.Types.map(t => t.value);
        }
        if (searchParamsForFilters.Distances && searchParamsForFilters.Distances.length > 0) {
          searchParamsForFilters.Distances = searchParamsForFilters.Distances.map(d => d.value);
        }
        if (searchParamsForFilters.Goings && searchParamsForFilters.Goings.length > 0) {
          searchParamsForFilters.Goings = searchParamsForFilters.Goings.map(g => g.value);
        }
        if (searchParamsForFilters.Surfaces && searchParamsForFilters.Surfaces.length > 0) {
          searchParamsForFilters.Surfaces = searchParamsForFilters.Surfaces.map(s => s.value);
        }
        if (searchParamsForFilters.Ages && searchParamsForFilters.Ages.length > 0) {
          searchParamsForFilters.Ages = searchParamsForFilters.Ages.map(a => a.value);
        }
        if (searchParamsForFilters.Sexes && searchParamsForFilters.Sexes.length > 0) {
          searchParamsForFilters.Sexes = searchParamsForFilters.Sexes.map(s => s.value);
        }
        if (searchParamsForFilters.Sires && searchParamsForFilters.Sires.length > 0) {
          searchParamsForFilters.Sires = searchParamsForFilters.Sires.map(s => s.value);
        }

        // Create system data
        const systemData = {
          name: systemForm.name,
          description: systemForm.description,
          filters: JSON.stringify(searchParamsForFilters),
          userId: userId,
          // Add metrics data
          startDate: startDate,
          endDate: endDate,
          totalSelections: totalItems.value,
          totalWinners: stats.totalWinners,
          strikeRate: stats.strikeRate,
          profitLoss: stats.totalProfitLoss
        };
        
        // Send to API
        await apiService.saveSystem(systemData);
        
        // Reset form and close modal
        systemForm.name = '';
        systemForm.description = '';
        showSaveSystemModal.value = false;
        
        // Show success message
        //alert('System saved successfully!');
      } catch (err) {
        console.error('Error saving system:', err);
        error.value = 'Failed to save system. Please try again.';
      } finally {
        savingSystem.value = false;
      }
    };
  
  
    
    return {
      searchParams,
      lastSearchedParams,
      results,
      loading,
      error,
      searched,
      currentPage,
      totalPages,
      totalItems,
      stats,
      dateErrors,
      dateRange,
      resetSearch,
      handleSearchSubmit,
      performSearch,
      changePage,
      formatDate,
      positionClass,
      getMaxDate,
      setLastWeek,
      setLastMonth,
      setLastSixMonth,
      validateDates,
      updateDateRange,
      formatDateForApi,
      
      // Multi-select options
      trainerOptions,
      jockeyOptions,
      courseOptions,
      regionOptions,
      typeOptions,
      distanceOptions,
      goingOptions,
      surfaceOptions,
      ageOptions,
      sexOptions,
      sireOptions,

      // Loading states
      isLoadingTrainers,
      isLoadingJockeys,
      isLoadingCourses,
      isLoadingRegions,
      isLoadingTypes,
      isLoadingDistances,
      isLoadingGoings,
      isLoadingSurfaces,
      isLoadingAges,
      isLoadingSexes,
      isLoadingSires,
      
      // Clear functions
      clearAllTrainers,
      clearAllJockeys,
      clearAllCourses,
      clearAllRegions,
      clearAllTypes,
      clearAllDistances,
      clearAllGoings,
      clearAllSurfaces,
      clearAllAges,
      clearAllSexes,
      clearAllSires,
      
      // Formatting
      limitText,
      
      // Selected values
      selectedTrainers,
      selectedJockeys,
      selectedCourses,
      selectedRegions,
      selectedTypes,
      selectedDistances,
      selectedGoings,
      selectedSurfaces,
      selectedAges,
      selectedSexes,
      selectedSires,
      
      // Debounced search functions
      searchTrainers,
      searchJockeys,
      searchCourses,
      searchRegions,
      searchTypes,
      searchDistances,
      searchGoings,
      searchSurfaces,
      searchAges,
      searchSexes,
      searchSires,

      monthlyStatsData,

       // System saving
       showSaveSystemModal,
      savingSystem,
      systemForm,
      openSaveSystemModal,
      saveSystem
    };
  }
};
</script>

<style scoped>

.search-results-page {
  min-height: calc(100vh - 120px);
}

.table-responsive {
  overflow-x: auto;
}

table {
  font-size: 0.9rem;
}

@media (max-width: 768px) {
  .d-flex.justify-content-between {
    flex-direction: column;
    gap: 1rem;
  }

  .search-form {
    width: 100%;
  }
}

.results-container {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Accordion animations */
.accordion-content {
  animation: accordion-down 0.2s ease-out;
}

:deep([data-state="closed"] .accordion-content) {
  animation: accordion-up 0.2s ease-out;
}

/* Rotate chevron when accordion is open */
:deep([data-state="open"] .accordion-chevron) {
  transform: rotate(180deg);
}

</style>
